import type { Address } from 'viem'

import { permissionedBackedTokens } from '@morpho-org/blue-sdk'
import { permissionedCoinbaseTokens } from '@morpho-org/blue-sdk'
import { ExternalLink } from '@morpho-org/ui-v6/server'
import { memo } from 'react'

import { useWeb3Context } from '@/contexts/Web3Context'

interface TransferErrorProps {
  tokenAddress: Address
}

/**
 * Component to display the appropriate error message for a transfer error
 *
 * @param tokenAddress - The address of the token to display the error message
 */
export const TransferError = memo(({ tokenAddress }: TransferErrorProps) => {
  const { targetChainId } = useWeb3Context()

  if (permissionedCoinbaseTokens[targetChainId]?.has(tokenAddress)) {
    return <CoinbaseAttestationRequired />
  }

  if (permissionedBackedTokens[targetChainId]?.has(tokenAddress)) {
    return <BackedAuthorizationRequired />
  }

  return <UnauthorizedTransfer />
})
TransferError.displayName = 'TransferError'

const UnauthorizedTransfer = () => (
  <span className="text-2xs text-destructive-foreground text-end">Authorization required.</span>
)

const BackedAuthorizationRequired = () => (
  <span className="text-2xs text-destructive-foreground text-end">
    Backed authorization required.{' '}
    <ExternalLink href="https://assets.backed.fi/onboarding" underlined>
      Learn more here →
    </ExternalLink>
  </span>
)

const CoinbaseAttestationRequired = () => (
  <span className="text-2xs text-destructive-foreground text-end">
    Coinbase attestation required.{' '}
    <ExternalLink href="https://www.coinbase.com/onchain-verify" underlined>
      Learn more here →
    </ExternalLink>
  </span>
)
